import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Lock, User as UserIcon, Building2, Shield, Check, X, MessageCircle, FileCheck, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { SedeService, UserService, ConsentService, legalDocTypeLabel } from "../services/apiService";
import { useToast } from '../components/ToastContext';
import { formatRole } from '../utils/utils';
import NotificheAreaToggles, { NotificheArea } from '../components/NotificheAreaToggles';

export const ProfiloView: React.FC = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { addToast } = useToast();

    const [nomeSede, setNomeSede] = useState<string>('Caricamento...');

    // Stati per Modal Password
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Consenso alle notifiche WhatsApp: parte dal valore del BE, il toggle usa l'endpoint dedicato.
    const [waOptIn, setWaOptIn] = useState<boolean>(!!user?.whatsappOptIn);
    const [waSaving, setWaSaving] = useState(false);
    useEffect(() => { setWaOptIn(!!user?.whatsappOptIn); }, [user?.whatsappOptIn]);

    // Avvisi da supervisore per area: solo titolare e responsabile.
    const isSupervisore = user?.role === 'ADMIN' || user?.role === 'RESPONSABILE';
    const [notifiche, setNotifiche] = useState<NotificheArea | null>(null);
    const [notificheSaving, setNotificheSaving] = useState(false);
    useEffect(() => {
        if (!isSupervisore) return;
        UserService.getNotificheArea()
            .then(r => { if (r?.data) setNotifiche(r.data); })
            .catch(() => { /* sezione non mostrata */ });
    }, [isSupervisore]);
    const handleNotifiche = async (next: NotificheArea) => {
        const prima = notifiche;
        setNotifiche(next);
        setNotificheSaving(true);
        try {
            const r = await UserService.updateNotificheArea(next);
            if (r?.data) setNotifiche(r.data);
            addToast("Preferenze notifiche aggiornate", "success");
        } catch {
            setNotifiche(prima);
            addToast("Errore salvataggio preferenze", "error");
        } finally {
            setNotificheSaving(false);
        }
    };

    // Numero destinatario delle notifiche WhatsApp, modificabile dall'utente.
    const [phone, setPhone] = useState<string>(user?.phone || '');
    const [phoneSaving, setPhoneSaving] = useState(false);
    useEffect(() => { setPhone(user?.phone || ''); }, [user?.phone]);
    // Il numero è visibile/modificabile solo quando l'istanza ha la WA attiva.
    const whatsappEnabled = !!user?.whatsappEnabled;

    const handleSavePhone = async () => {
        if (phoneSaving) return;
        setPhoneSaving(true);
        try {
            await UserService.updatePhone(phone.trim());
            addToast("Numero di telefono aggiornato", "success");
        } catch (error: any) {
            addToast(error.response?.data?.message || "Errore nell'aggiornamento del numero", "error");
        } finally {
            setPhoneSaving(false);
        }
    };

    // Documenti legali accettati ed eventuali documenti ancora da accettare.
    const [consents, setConsents] = useState<{ pending: any[]; accepted: any[] } | null>(null);
    // Click su una riga: apre il testo della versione accettata.
    const [readerDoc, setReaderDoc] = useState<any | null>(null);
    const [readerLoading, setReaderLoading] = useState(false);
    const openConsentDoc = async (documentId: number) => {
        setReaderLoading(true);
        try {
            const doc = await ConsentService.getConsentedDocument(documentId);
            if (doc) setReaderDoc(doc);
        } catch { /* silenzioso */ }
        finally { setReaderLoading(false); }
    };
    useEffect(() => {
        let alive = true;
        ConsentService.getStatus()
            .then(s => { if (alive) setConsents({ pending: s.pending || [], accepted: s.accepted || [] }); })
            .catch(() => { /* silenzioso: sezione non mostrata se non caricabile */ });
        return () => { alive = false; };
    }, []);

    const handleToggleWhatsapp = async () => {
        if (waSaving) return;
        const next = !waOptIn;
        setWaSaving(true);
        setWaOptIn(next); // optimistic
        try {
            await UserService.updateWhatsappOptIn(next);
            addToast(next ? "Notifiche WhatsApp attivate" : "Notifiche WhatsApp disattivate", "success");
        } catch (error: any) {
            setWaOptIn(!next); // rollback
            addToast("Errore nell'aggiornamento della preferenza", "error");
        } finally {
            setWaSaving(false);
        }
    };

    useEffect(() => {
        const fetchSedeInfo = async () => {
            if (!user) return;
            if (user.sedeId) {
                try {
                    const res = await SedeService.getById(user.sedeId);
                    if (res.success && res.data) setNomeSede(res.data.name);
                    else setNomeSede(`Sede #${user.sedeId} (Non trovata)`);
                } catch (error) { setNomeSede("Errore caricamento sede"); }
            } else { setNomeSede('Tutte le Sedi (Global)'); }
        };
        fetchSedeInfo();
    }, [user]);

    const handleLogout = () => { logout(); navigate('/login'); };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            addToast("Le nuove password non coincidono", "error");
            return;
        }
        if (newPassword.length < 8) {
            addToast("La password deve essere di almeno 8 caratteri", "error");
            return;
        }

        setIsSaving(true);
        try {
            await UserService.changePassword({ oldPassword, newPassword, confirmPassword });
            addToast("Password aggiornata con successo", "success");
            setIsPasswordModalOpen(false);
            setOldPassword(''); setNewPassword(''); setConfirmPassword('');
        } catch (error: any) {
            addToast(error.response?.data?.message || "Errore durante il cambio password", "error");
        } finally {
            setIsSaving(false);
        }
    };

    if (!user) return <div className="p-6 text-center">Caricamento profilo...</div>;

    const initials = `${user.name?.charAt(0) || ''}${user.surname?.charAt(0) || ''}`.toUpperCase();

    return (
        <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Il Mio Profilo</h1>

            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4 shadow-lg border-4 border-white">
                    {initials}
                </div>

                <h2 className="text-2xl font-bold text-gray-900">{user.name} {user.surname}</h2>

                <div className="flex flex-wrap justify-center items-center gap-2 mt-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full uppercase flex items-center gap-1">
                        <Shield size={12}/> {formatRole(user.role)}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full flex items-center gap-1">
                        <Building2 size={12}/> {nomeSede}
                    </span>
                </div>

                <div className="w-full max-w-md space-y-6 mt-8">
                    <div className="text-left">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <UserIcon size={16} className="text-gray-400"/> Email (Accesso)
                        </label>
                        <input type="email" value={user.email} className="w-full p-3 border rounded-lg bg-gray-50 text-gray-600 outline-none cursor-not-allowed" readOnly />
                    </div>

                    <div className="text-left">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <Lock size={16} className="text-gray-400"/> Sicurezza
                        </label>
                        <div className="flex justify-between items-center p-3 border rounded-lg bg-gray-50">
                            <span className="text-gray-500 font-mono text-lg leading-none mt-1">••••••••</span>
                            <button onClick={() => setIsPasswordModalOpen(true)} className="text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md text-sm font-bold hover:bg-blue-100 transition-colors">
                                Cambia Password
                            </button>
                        </div>
                    </div>

                    {isSupervisore && notifiche && (
                        <div className="text-left">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notifiche</label>
                            <NotificheAreaToggles value={notifiche} onChange={handleNotifiche} disabled={notificheSaving} />
                        </div>
                    )}

                    {/* Notifiche WhatsApp, solo se l'istanza le ha attive: consenso + numero destinatario. */}
                    {whatsappEnabled && (
                        <div className="text-left">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                                <MessageCircle size={16} className="text-green-500"/> Notifiche WhatsApp
                            </label>
                            <div className="p-3 border rounded-lg bg-gray-50 space-y-3">
                                <div className="flex justify-between items-center gap-3">
                                    <span className="text-sm text-gray-600">Ricevi notifiche via WhatsApp</span>
                                    <button
                                        type="button"
                                        onClick={handleToggleWhatsapp}
                                        disabled={waSaving}
                                        role="switch"
                                        aria-checked={waOptIn}
                                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${waOptIn ? 'bg-green-500' : 'bg-gray-300'}`}
                                    >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${waOptIn ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <div className="border-t border-gray-100 pt-3">
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Numero di telefono</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={e => setPhone(e.target.value)}
                                            placeholder="+39 333 1234567"
                                            className="flex-1 p-2.5 border rounded-lg outline-none focus:ring-2 ring-green-500 bg-white"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSavePhone}
                                            disabled={phoneSaving || phone.trim() === (user?.phone || '').trim()}
                                            className="px-3 py-2 rounded-lg text-sm font-bold bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {phoneSaving ? 'Salvo…' : 'Salva'}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Usato solo per recapitarti le notifiche di servizio. Nessun dato del caso viene inviato via WhatsApp.</p>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* I miei consensi: documenti accettati (con data) ed eventuali pendenti. */}
            {consents && (consents.accepted.length > 0 || consents.pending.length > 0) && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-1">
                        <FileCheck size={18} className="text-blue-600" /> I miei consensi
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">Documenti legali che hai accettato. Le versioni sono archiviate in forma immutabile.</p>

                    {consents.pending.some((d: any) => d.mandatory) && (
                        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <span>Hai documenti obbligatori ancora da accettare: al prossimo accesso ti verrà chiesto il consenso.</span>
                        </div>
                    )}

                    {consents.accepted.length > 0 ? (
                        <ul className="divide-y divide-gray-100">
                            {consents.accepted.map((c: any, i: number) => (
                                <li key={`${c.documentId}-${i}`}>
                                    <button
                                        type="button"
                                        onClick={() => openConsentDoc(c.documentId)}
                                        title="Leggi il documento"
                                        className="w-full py-2.5 flex items-center justify-between gap-3 text-left hover:bg-gray-50 rounded-md px-1 transition-colors"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-800 truncate">{legalDocTypeLabel(c.documentType)}</div>
                                            <div className="text-xs text-gray-400">Versione {c.documentVersion} · Clicca per leggere</div>
                                        </div>
                                        <div className="text-xs text-gray-500 shrink-0 flex items-center gap-1">
                                            <Check size={14} className="text-emerald-500" />
                                            {c.acceptedAt ? new Date(c.acceptedAt).toLocaleString('it-IT') : ''}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-400">Nessun consenso ancora registrato.</p>
                    )}
                </div>
            )}

            <button onClick={handleLogout} className="w-full sm:w-auto mx-auto mt-6 bg-red-50 text-red-600 font-bold hover:bg-red-100 px-8 py-4 rounded-xl transition-colors flex items-center justify-center gap-2 border border-red-200">
                <LogOut size={20}/> Esci dal Gestionale
            </button>

            {/* Modale lettore documento consenso */}
            {readerDoc && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setReaderDoc(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-gray-100 flex justify-between items-center gap-3 bg-gray-50">
                            <div className="min-w-0">
                                <div className="text-lg font-bold text-gray-900 truncate">{readerDoc.title}</div>
                                <div className="text-xs text-gray-500">Versione {readerDoc.version}</div>
                            </div>
                            <button type="button" onClick={() => setReaderDoc(null)} className="text-gray-500 hover:text-gray-900 font-bold shrink-0">Chiudi</button>
                        </div>
                        <div
                            className="p-6 overflow-y-auto text-sm text-gray-800 leading-relaxed [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:mt-3 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_table]:w-full [&_td]:border-b [&_td]:border-gray-100 [&_td]:py-1 [&_a]:text-blue-600"
                            dangerouslySetInnerHTML={{ __html: readerDoc.contentHtml || '' }}
                        />
                    </div>
                </div>
            )}
            {readerLoading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div className="bg-white px-5 py-3 rounded-lg shadow text-sm font-semibold text-gray-700">Apertura documento…</div>
                </div>
            )}

            {/* Modale cambio password */}
            {isPasswordModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
                        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h2 className="text-lg font-bold text-gray-900">Modifica Password</h2>
                            <button onClick={() => setIsPasswordModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                        </div>

                        <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Password Attuale</label>
                                <input required type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} className="w-full p-3 border rounded-lg outline-none focus:ring-2 ring-blue-500"/>
                            </div>
                            <div className="border-t border-gray-100 pt-4">
                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Nuova Password</label>
                                <input required type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full p-3 border rounded-lg outline-none focus:ring-2 ring-blue-500"/>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Conferma Nuova Password</label>
                                <input required type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full p-3 border rounded-lg outline-none focus:ring-2 ring-blue-500"/>
                            </div>

                            <button type="submit" disabled={isSaving} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold mt-6 shadow-md hover:bg-blue-700 disabled:opacity-50">
                                {isSaving ? 'Salvataggio...' : 'Aggiorna Password'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfiloView;